/**
 * Restructure subscription plan categories for SubscriptionListScreen: tags and tagged views.
 * Includes processSubscriptionData (normalize, sort, filter), "Others" tag, and Flow 2 hierarchy.
 */
import { applyV3CatalogPlanDayOffFields } from './catalogPlanDayOff';

export interface RestructuredTag {
  tag_id: string;
  tag_name: string;
  tag_attachment?: unknown;
  display_order?: number;
  displayOrder?: number;
}

export interface CategoryWithDays {
  id?: number | string;
  title?: string;
  description?: string;
  attachment?: string;
  thumbnail?: string;
  days: Array<{
    day: number;
    type_key?: number;
    duration?: number;
    subscriptions: any[];
  }>;
  subscriptions?: any[];
}

export interface TaggedCategory {
  tag_id: string;
  tag_name?: string;
  tag_attachment?: unknown;
  categories: CategoryWithDays[];
}

export interface RestructuredData {
  metadata: {
    tags: RestructuredTag[];
  };
  tagged_categories: TaggedCategory[];
}

/** Sort by display_order (asc); default 9999 if missing. */
export function sortByDisplayOrder<T extends { display_order?: number; displayOrder?: number }>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const order_a = a.display_order ?? a.displayOrder ?? 9999;
    const order_b = b.display_order ?? b.displayOrder ?? 9999;
    return Number(order_a) - Number(order_b);
  });
}

const OTHERS_TAG_ID = '0';
const OTHERS_TAG: RestructuredTag = { tag_id: OTHERS_TAG_ID, tag_name: 'Others', tag_attachment: undefined };

function hasValidTag(plan: any): boolean {
  const tag_id = plan.tag_id;
  const tag_name = plan.tag_name;
  if (tag_id != null && Number(tag_id) > 0 && tag_name) {return true;}
  if (tag_name && !tag_id) {return true;}
  return false;
}

function collectTags(categories: any[]): RestructuredTag[] {
  const seen = new Map<string, RestructuredTag>();
  let has_untagged = false;
  for (const cat of categories) {
    const subs = cat.subscriptions ?? [];
    for (const sub of subs) {
      const type_data = sub.type_data ?? [];
      for (const plan of type_data) {
        if (!hasValidTag(plan)) {
          has_untagged = true;
          continue;
        }
        const tag_id = String(plan.tag_id ?? plan.tag_name ?? '');
        if (tag_id && !seen.has(tag_id)) {
          seen.set(tag_id, {
            tag_id,
            tag_name: plan.tag_name ?? String(plan.tag_id ?? tag_id),
            tag_attachment: plan.tag_attachment,
          });
        }
      }
    }
  }
  const list = Array.from(seen.values());
  const sorted = sortByDisplayOrder(list);
  if (has_untagged) {
    sorted.push(OTHERS_TAG);
  }
  return sorted;
}

function planMatchesTag(plan: any, tag_id: string): boolean {
  if (tag_id === OTHERS_TAG_ID) {
    return !hasValidTag(plan);
  }
  return String(plan.tag_id ?? plan.tag_name) === String(tag_id);
}

function buildTaggedCategoriesForTag(categories: any[], tag_id: string): CategoryWithDays[] {
  const result: CategoryWithDays[] = [];
  for (const cat of categories) {
    const subs = cat.subscriptions ?? [];
    const by_day = new Map<number, any[]>();
    for (const sub of subs) {
      const day_val = Number(sub.days ?? sub.type_key ?? sub.duration ?? 0);
      if (day_val <= 0) {
        continue;
      }
      const type_data = (sub.type_data ?? []).filter((plan: any) => planMatchesTag(plan, tag_id));
      if (type_data.length === 0) {
        continue;
      }
      if (!by_day.has(day_val)) {
        by_day.set(day_val, []);
      }
      by_day.get(day_val)!.push(
        ...type_data.map((p: any) =>
          applyV3CatalogPlanDayOffFields(
            { ...p, day: day_val, type_key: day_val, days: day_val, category_id: cat.id, subscription_category_id: cat.id },
            cat,
          ),
        ),
      );
    }
    if (by_day.size === 0) {
      continue;
    }
    const days = Array.from(by_day.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([day_val, subscriptions]) => ({
        day: day_val,
        type_key: day_val,
        duration: day_val,
        subscriptions,
      }));
    result.push({
      ...cat,
      id: cat.id,
      days,
      subscriptions: cat.subscriptions,
    });
  }
  return result;
}

/**
 * Build tagged metadata and per-tag category views for the subscription list screen (Flow 1).
 */
export function restructureSubscriptionData(subs: any[]): RestructuredData {
  const categories = Array.isArray(subs) ? subs : [];
  const tags = collectTags(categories);

  const tagged_categories: TaggedCategory[] = tags.map(tag => ({
    tag_id: tag.tag_id,
    tag_name: tag.tag_name,
    tag_attachment: tag.tag_attachment,
    categories: buildTaggedCategoriesForTag(categories, tag.tag_id),
  }));

  return {
    metadata: { tags },
    tagged_categories,
  };
}

/** Normalize, sort, filter raw subscription_categories. Use before restructure. */
export function processSubscriptionData(raw_categories: any[]): {
  regular_plans: any[];
  custom_plans: any[];
  has_custom_plans: boolean;
} {
  const list = Array.isArray(raw_categories) ? raw_categories : [];
  const regular_plans: any[] = [];
  const custom_plans: any[] = [];

  for (const cat of list) {
    if (cat.type === 'custom') {
      custom_plans.push({ ...cat });
    } else {
      regular_plans.push({ ...cat });
    }
  }

  const sort_regular = sortByDisplayOrder(regular_plans);
  const sort_custom = sortByDisplayOrder(custom_plans);

  for (const cat of sort_regular) {
    const subs = cat.subscriptions ?? [];
    for (const sub of subs) {
      sub.days = Number(sub.type_key ?? sub.days ?? 0);
      delete sub.type_name;
    }
    const filtered_subs = subs.filter(
      (sub: any) => (sub.type_data ?? []).length > 0,
    );
    cat.subscriptions = filtered_subs;
  }

  const empty_category = (cat: any) => {
    const subs = cat.subscriptions ?? [];
    if (!subs.length) {return true;}
    if (subs.length === 1) {return false;}
    return subs.slice(1).every((sub: any) => !(sub.type_data?.length));
  };
  const filtered_regular = sort_regular.filter(c => !empty_category(c));

  return {
    regular_plans: filtered_regular,
    custom_plans: sort_custom,
    has_custom_plans: sort_custom.length > 0,
  };
}

export interface CategoryDayTaggedPlans {
  tag_id: string;
  tag_name: string;
  tag_attachment?: unknown;
  plans: any[];
}

export interface CategoryDayEntry {
  day: number;
  type_key: number;
  tagged_plans: CategoryDayTaggedPlans[];
}

export interface CategoryDaysItem {
  id?: number | string;
  title?: string;
  description?: string;
  attachment?: string;
  display_order?: number;
  days: CategoryDayEntry[];
}

export interface CategoryDaysData {
  categories: CategoryDaysItem[];
  metadata: { tags: RestructuredTag[] };
}

/**
 * Restructure for Flow 2 (Category → Days → Tag → Plan). Use when organization.show_subscriptions === 'Yes'.
 */
export function restructureForCategoryDaysHierarchy(regular_plans: any[]): CategoryDaysData {
  const categories: CategoryDaysItem[] = [];
  const tags_seen = new Map<string, RestructuredTag>();

  for (const cat of regular_plans) {
    const by_day = new Map<number, Map<string, any[]>>();
    const subs = cat.subscriptions ?? [];
    for (const sub of subs) {
      const day_val = Number(sub.days ?? sub.type_key ?? 0);
      if (day_val <= 0) {continue;}
      const type_data = sub.type_data ?? [];
      for (const plan of type_data) {
        const extended = applyV3CatalogPlanDayOffFields(
          {
            ...plan,
            category_id: cat.id,
            subscription_category_id: cat.id,
            type_key: day_val,
            days: day_val,
          },
          cat,
        );
        const tag_id = hasValidTag(plan) ? String(plan.tag_id ?? plan.tag_name) : OTHERS_TAG_ID;
        const tag_name = hasValidTag(plan) ? (plan.tag_name ?? String(plan.tag_id)) : 'Others';
        const tag_attachment = plan.tag_attachment;
        if (!tags_seen.has(tag_id)) {
          tags_seen.set(tag_id, { tag_id, tag_name, tag_attachment });
        }
        if (!by_day.has(day_val)) {by_day.set(day_val, new Map());}
        const day_map = by_day.get(day_val)!;
        if (!day_map.has(tag_id)) {day_map.set(tag_id, []);}
        day_map.get(tag_id)!.push(extended);
      }
    }
    const days: CategoryDayEntry[] = Array.from(by_day.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([day_val, tag_map]) => ({
        day: day_val,
        type_key: day_val,
        tagged_plans: Array.from(tag_map.entries()).map(([tid, plans]) => {
          const t = tags_seen.get(tid) ?? { tag_id: tid, tag_name: tid };
          return {
            tag_id: tid,
            tag_name: t.tag_name,
            tag_attachment: t.tag_attachment,
            plans,
          };
        }),
      }));
    if (days.length > 0) {
      categories.push({
        id: cat.id,
        title: cat.title,
        description: cat.description,
        attachment: cat.attachment,
        display_order: cat.display_order,
        days,
      });
    }
  }

  const tags_list = sortByDisplayOrder(Array.from(tags_seen.values()));
  if (!tags_list.find(t => t.tag_id === OTHERS_TAG_ID)) {
    tags_list.push(OTHERS_TAG);
  }
  return {
    categories: sortByDisplayOrder(categories),
    metadata: { tags: tags_list },
  };
}

/** List row in subscription SectionList / modals. */
export interface PlanItem {
  key: string;
  plan: any;
  days: number;
  categoryTitle: string;
  categoryImage?: string;
}

export interface PlanSection {
  id: string;
  title: string;
  arabic_title?: string;
  description?: string;
  arabic_description?: string;
  attachment?: string;
  data: PlanItem[];
}

export function buildSections(categories: any[], selectedTagId: string | null, restructuredData: RestructuredData | null): PlanSection[] {
  const useTagged = restructuredData && selectedTagId && selectedTagId !== 'all-0';
  const categoriesToUse = useTagged
    ? (restructuredData!.tagged_categories.find((t: TaggedCategory) => t.tag_id === selectedTagId)?.categories ?? [])
    : categories;

  const sections: PlanSection[] = [];

  for (const category of categoriesToUse) {
    const items: PlanItem[] = [];

    if (useTagged && (category as CategoryWithDays).days) {
      for (const dayEntry of (category as CategoryWithDays).days) {
        const dayVal = Number(dayEntry.day ?? dayEntry.type_key ?? dayEntry.duration ?? 0);
        if (dayVal <= 0) {
          continue;
        }
        const plans = (dayEntry as { subscriptions?: any[] }).subscriptions ?? [];
        for (const plan of plans) {
          if (plan.is_active === false) {
            continue;
          }
          items.push({
            key: `plan-${category.id}-${dayVal}-${plan.subscription_id ?? plan.id}`,
            plan: applyV3CatalogPlanDayOffFields(
              {
                ...plan,
                days: dayVal,
                type_key: dayVal,
                duration: dayVal,
                category_id: category.id,
                subscription_category_id: category.id,
              },
              category,
            ),
            days: dayVal,
            categoryTitle: category.title ?? 'Plan',
            categoryImage: category.attachment ?? category.thumbnail,
          });
        }
      }
    } else {
      const subs = category.subscriptions ?? [];
      for (const sub of subs) {
        const dayVal = Number(sub.days ?? sub.type_key ?? sub.duration ?? 0);
        if (dayVal <= 0) {
          continue;
        }
        const typeData = sub.type_data ?? [];
        for (const plan of typeData) {
          if (plan.is_active === false) {
            continue;
          }
          items.push({
            key: `plan-${category.id}-${dayVal}-${plan.subscription_id ?? plan.id}`,
            plan: applyV3CatalogPlanDayOffFields(
              {
                ...plan,
                days: dayVal,
                type_key: dayVal,
                duration: dayVal,
                category_id: category.id,
                subscription_category_id: category.id,
              },
              category,
            ),
            days: dayVal,
            categoryTitle: category.title ?? 'Plan',
            categoryImage: category.attachment ?? category.thumbnail,
          });
        }
      }
    }

    if (items.length > 0) {
      sections.push({
        id: String(category.id ?? sections.length),
        title: category.title ?? 'Plans',
        arabic_title: category.arabic_title,
        description: category.description,
        arabic_description: category.arabic_description,
        attachment: category.attachment ?? category.thumbnail,
        data: items,
      });
    }
  }

  return sections;
}

export interface BannerSlideMetrics {
  plan_count: number;
  duration_count: number;
  category_count: number;
  min_days: number | null;
  max_days: number | null;
}

function finalizeBannerMetrics(plan_count: number, days: Set<number>, category_count: number): BannerSlideMetrics {
  const sorted_days = Array.from(days).sort((a, b) => a - b);
  return {
    plan_count,
    duration_count: sorted_days.length,
    category_count,
    min_days: sorted_days.length > 0 ? sorted_days[0] : null,
    max_days: sorted_days.length > 0 ? sorted_days[sorted_days.length - 1] : null,
  };
}

/** Metrics for tag / “All” carousel slides (see-more sheet). */
export function computeTagBannerMetrics(
  tag_id: string,
  restructured_data: RestructuredData | null,
  regular_plans: any[],
): BannerSlideMetrics {
  const days = new Set<number>();
  let plan_count = 0;
  let category_count = 0;

  if (String(tag_id) === 'all-0') {
    for (const cat of regular_plans) {
      category_count += 1;
      for (const sub of cat.subscriptions ?? []) {
        const day_val = Number(sub.days ?? sub.type_key ?? sub.duration ?? 0);
        if (day_val > 0) {
          days.add(day_val);
        }
        plan_count += (sub.type_data ?? []).length;
      }
    }
    return finalizeBannerMetrics(plan_count, days, category_count);
  }

  const tagged = restructured_data?.tagged_categories?.find(t => String(t.tag_id) === String(tag_id));
  if (!tagged) {
    return finalizeBannerMetrics(0, days, 0);
  }
  category_count = tagged.categories.length;
  for (const cat of tagged.categories) {
    for (const day_entry of cat.days ?? []) {
      const day_val = Number(day_entry.day ?? day_entry.type_key ?? 0);
      if (day_val > 0) {
        days.add(day_val);
      }
      plan_count += (day_entry.subscriptions ?? []).length;
    }
  }
  return finalizeBannerMetrics(plan_count, days, category_count);
}

/** Metrics for category-hierarchy carousel slides (see-more sheet). */
export function computeCategoryBannerMetrics(category: CategoryDaysItem): BannerSlideMetrics {
  const days = new Set<number>();
  let plan_count = 0;
  for (const day_entry of category.days ?? []) {
    const day_val = Number(day_entry.day ?? day_entry.type_key ?? 0);
    if (day_val > 0) {
      days.add(day_val);
    }
    let day_plan_count = 0;
    for (const tagged of day_entry.tagged_plans ?? []) {
      day_plan_count += tagged.plans?.length ?? 0;
    }
    if (day_plan_count === 0) {
      day_plan_count += (day_entry as { subscriptions?: unknown[] }).subscriptions?.length ?? 0;
    }
    plan_count += day_plan_count;
  }
  return finalizeBannerMetrics(plan_count, days, 1);
}

export function getUniqueSortedDaysFromSection(section: PlanSection | null): number[] {
  if (!section?.data?.length) {
    return [];
  }
  const set = new Set<number>();
  section.data.forEach(item => set.add(item.days));
  return Array.from(set).sort((a, b) => a - b);
}

/**
 * Flatten a category/day entry into catalog `PlanItem`s for merged (and modal) plan lists.
 */
export function buildPlanItemsFromCategoryDayEntry(
  day_entry: CategoryDayEntry | null | undefined,
  category_title: string,
): PlanItem[] {
  if (!day_entry) {
    return [];
  }
  const day_val = Number(day_entry.day ?? day_entry.type_key ?? 0);
  if (day_val <= 0) {
    return [];
  }
  const items: PlanItem[] = [];
  for (const tagged of day_entry.tagged_plans ?? []) {
    for (const plan of tagged.plans ?? []) {
      if (plan?.is_active === false) {
        continue;
      }
      items.push({
        key: `merged-${day_val}-${String(tagged.tag_id ?? 'tag')}-${String(plan.subscription_id ?? plan.id ?? items.length)}`,
        plan: { ...plan, days: day_val, type_key: day_val },
        days: day_val,
        categoryTitle: category_title,
        categoryImage: plan.thumbnail ?? plan.attachment,
      });
    }
  }
  return items;
}

/**
 * One batch of derived models for `SubscriptionListScreen` (replaces 3 separate useMemos over the same `regular_plans` input).
 * - **Tag flow** — `restructuredData` (`restructureSubscriptionData`) for tag chips and tagged plan slices.
 * - **Category / merged flow** — `categoryDaysData` when hierarchy is enabled; hierarchy: category → day → tag → plan.
 * - **Flat flow** — `sections` from `buildSections` (per-category `PlanSection` rows) for the tag/flat list UIs.
 */
export function buildSubscriptionListViewModels(
  regular_plans: any[],
  selectedTagId: string | null,
  use_category_days_hierarchy: boolean,
): {
  restructuredData: RestructuredData | null;
  categoryDaysData: CategoryDaysData | null;
  sections: PlanSection[];
} {
  if (!Array.isArray(regular_plans) || regular_plans.length === 0) {
    return { restructuredData: null, categoryDaysData: null, sections: [] };
  }
  const restructuredData = restructureSubscriptionData(regular_plans);
  const categoryDaysData = use_category_days_hierarchy ? restructureForCategoryDaysHierarchy(regular_plans) : null;
  const sections = buildSections(regular_plans, selectedTagId, restructuredData);
  return { restructuredData, categoryDaysData, sections };
}
